import React, { useRef, useEffect, useContext } from "react";
import { T, useTranslateKey } from "../../hooks/useTranslate";
import { useMixPanel } from "../../hooks/useMixPanel";
import { useHelpCenterUrl } from "../../hooks/useHelpCenterUrl";
import {
  Box,
  Stack,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Link,
} from "@mui/material";
import { useAuth } from "../../hooks/useAuth.js";
import { LanguageContext } from "../../hooks/LanguageContext";
import { ActionableIndicator } from "../../components/ActionableIndicator";
import { useActionableItemsContext } from "../../hooks/ActionableItemsContext";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { ReactComponent as ExternalIcon } from "../../assets/icons/external.svg";
import { useLocation } from "react-router-dom";
import { Typography } from "@unicity/design-system";

const ResourceNavItem = ({
  open,
  item,
  drawerOpen,
  isMobile,
  setDrawerWidth,
  shopLink,
}) => {
  const elementRef = useRef(null);
  const { userInfo } = useAuth();
  const translatedItemTitle = useTranslateKey(item.text);
  const { logEvent } = useMixPanel();
  const { languageDir } = useContext(LanguageContext);
  const { isActionable } = useActionableItemsContext();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // widen the drawer if the text box breaks the 102 threshold
  useEffect(() => {
    if (elementRef.current) {
      const width = elementRef.current.getBoundingClientRect().width;
      if (width > 105) {
        setDrawerWidth(width + 150);
      }
    }
    // eslint-disable-next-line
  }, []);

  const distributorHelpCenterUrl = useHelpCenterUrl("home");

  const getResourceHref = (value) => {
    switch (value) {
      case "external-help":
        return distributorHelpCenterUrl;
      case "shop-unicity":
        return shopLink;
      case "shop-feel-great":
        return item.externalUrl;
      default:
        return "";
    }
  };

  const handleNavClick = () => {
    const status = userInfo.status;
    logEvent(item.eventName, {
      location: "menu",
      status,
      id: userInfo.unicityId,
    });

    if (status === "Suspended" || status === "Terminated") {
      localStorage.setItem("showSuspendedTerminatedError", true);
      navigate("/login");
      return;
    }

    // Handle library navigation
    if (item.value === "library") {
      navigate(item.url);
      return;
    }

    const resourceHref = getResourceHref(item.value);
    if (!resourceHref) return false;
    window.open(resourceHref, "_blank");
  };

  const commonProps = {
    target: "_blank",
    rel: "noreferrer",
    sx: { textDecoration: "none" },
  };

  const isSelected = () => {
    if (item.value === "library" && location.pathname.includes("/library")) {
      return true;
    }
    return false;
  };

  const renderExternalIcon = () => {
    // Library is an internal link, no external icon
    if (item.value === "library") {
      return null;
    }

    return (
      <Box>
        <ExternalIcon
          className="external-icon"
          style={{
            transform: languageDir === "rtl" ? "rotateY(180deg)" : "none",
          }}
        />
      </Box>
    );
  };

  return (
    <Link
      {...commonProps}
      onClick={handleNavClick}
      data-test-id={`${item.value}-nav-item-link-element`}
      className="resource-nav-item-link"
    >
      <ListItem
        id={`${item.value}-nav-item`}
        data-test-id={`${item.value}-nav-item`}
        title={translatedItemTitle}
        aria-label={item.text}
        value={item.value}
        disablePadding
        sx={{ display: "block" }}
      >
        <ListItemButton
          sx={{
            minHeight: 48,
            justifyContent: isMobile ? "initial" : "center",
            px: isMobile ? 1.2 : 2.5,
            mb: isMobile ? "0px" : "10px",
            pointerEvents: isSelected() ? "none" : "auto",
            cursor: isSelected() ? "default" : "pointer",
          }}
          selected={isSelected()}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: isMobile ? (drawerOpen ? 3 : "auto") : open ? 3 : "auto",
              justifyContent: "center",
            }}
          >
            <ActionableIndicator
              padRight={-6}
              invisible={
                !(
                  isActionable(item.text) && !(isMobile ? !!drawerOpen : !!open)
                )
              }
            >
              {getIcon(item.value, isSelected())}
            </ActionableIndicator>
          </ListItemIcon>
          <ListItemText
            primary={
              <ActionableIndicator invisible={!isActionable(item.text)}>
                <Box
                  sx={{ display: "flex", gap: "4px", alignItems: "flex-start" }}
                >
                  <Stack flexDirection="row" gap={0.5} ref={elementRef}>
                    <T>{item.text}</T>
                    {item.tags?.includes("beta") && (
                      <Stack
                        justifyContent="center"
                        sx={{
                          fontSize: "calc(8px * var(--unicity-font-scale, 1))",
                          padding: "2px 6px",
                          color: theme.beta.default.color,
                          backgroundColor: theme.beta.default.backgroundColor,
                          borderRadius: "3px",
                        }}
                      >
                        <Typography
                          variant="small"
                          sx={{
                            display: "inline",
                            color: `${theme.beta.default.color} !important`,
                            fontSize:
                              "calc(8px * var(--unicity-font-scale, 1))",
                          }}
                        >
                          <T>beta</T>
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                  {renderExternalIcon()}
                </Box>
              </ActionableIndicator>
            }
            primaryTypographyProps={{
              fontFamily: "Inter",
              fontWeight: "600",
              fontSize: "calc(12px * var(--unicity-font-scale, 1))",
              color: isSelected() ? "#009ECF" : null,
            }}
            sx={{ opacity: isMobile ? (drawerOpen ? 1 : 0) : open ? 1 : 0 }}
          />
        </ListItemButton>
      </ListItem>
    </Link>
  );
};

const getIcon = (val, selected) => {
  switch (val) {
    case "library":
      return (
        <Box sx={{ display: "inline" }}>
          <img
            src={`icons/library-icon${selected ? "-selected" : ""}.svg`}
            alt="library icon"
          />
        </Box>
      );
    case "external-help":
      return (
        <Box sx={{ display: "inline" }}>
          <img src="icons/external-help.svg" alt="help icon" />
        </Box>
      );
    case "shop-feel-great":
      return (
        <Box sx={{ display: "inline" }}>
          <img src="icons/shop-feel-great.svg" alt="shop feel great icon" />
        </Box>
      );
    case "shop-unicity":
      return (
        <Box sx={{ display: "inline" }}>
          <img src="icons/shop-unicity.svg" alt="shop unicity icon" />
        </Box>
      );
    default:
      throw new Error("Icon not found.");
  }
};

export { ResourceNavItem };
